import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { COMPANY_INFO, LOGO_URL } from '../../data/company';
import { getCategoryById, getProductBySlug } from '../../data/products';
import { SUPPORT_FAQS, getProductFaqs } from '../../data/supportFaqs';
import { getRouteMeta } from '../../routeMeta';

const siteUrl = 'https://www.leiongroup.com';
const organizationDescription =
  'LEION Engineering manufactures and supplies industrial weighing systems, weighbridges, truck scales, indicators, platform scales, and weighing software for industrial operations in Ghana, India, Africa, Dubai, and global markets.';
// ISO 3166-1 codes, priority order: primary market (GH), then IN, AE, SA, West Africa, East/Southern Africa, Gulf, Asia.
const areaServed = [
  'GH', 'IN', 'AE', 'SA',
  'NG', 'CI', 'TG', 'BJ', 'BF', 'SL', 'LR', 'GM', 'GN', 'SN', 'ML',
  'KE', 'TZ', 'UG', 'ZM', 'ET', 'MZ',
  'OM', 'QA', 'KW', 'BH',
  'NP', 'BD', 'LK', 'TW',
];

const setJsonLd = (id, schema) => {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(schema);
};

const removeElement = (id) => {
  document.getElementById(id)?.remove();
};

const SiteMetadata = () => {
  const { pathname } = useLocation();
  const primaryPhone = COMPANY_INFO.contact.phone?.[0];
  const primaryEmail = COMPANY_INFO.contact.email?.[0];
  const pathSegments = pathname.split('/').filter(Boolean);
  const productSlug = pathSegments[0] === 'products' && pathSegments[1] === 'p' ? pathSegments[2] : null;
  const product = productSlug ? getProductBySlug(productSlug) : null;
  const categoryId = pathSegments[0] === 'products' && pathSegments[1] !== 'p' ? pathSegments[1] : null;
  const category = categoryId ? getCategoryById(categoryId) : null;

  // FAQ schema lists exactly the FAQs visible on the page.
  const pageFaqs = useMemo(() => (pathname === '/' || pathname === '/support'
    ? SUPPORT_FAQS
    : productSlug ? getProductFaqs(productSlug) : []), [pathname, productSlug]);

  const metadata = getRouteMeta(pathname);
  const canonicalUrl = pathname === '/' ? `${siteUrl}/` : `${siteUrl}${pathname}`;

  const organizationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': ['Organization', 'Manufacturer'],
    '@id': `${siteUrl}/#organization`,
    name: COMPANY_INFO.name,
    alternateName: ['LEION Group', 'Leiongroup'],
    url: `${siteUrl}/`,
    logo: `${siteUrl}${LOGO_URL}`,
    slogan: COMPANY_INFO.tagline,
    foundingDate: COMPANY_INFO.established,
    ...(primaryEmail ? { email: primaryEmail } : {}),
    ...(primaryPhone ? { telephone: primaryPhone } : {}),
    description: organizationDescription,
    disambiguatingDescription:
      'Indian manufacturer of weighbridges, truck scales and weighing software, headquartered in Gurgaon, Haryana, serving Ghana and West Africa.',
    areaServed,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      url: `${siteUrl}/contact`,
      ...(primaryEmail ? { email: primaryEmail } : {}),
      ...(primaryPhone ? { telephone: primaryPhone } : {}),
      areaServed,
      availableLanguage: ['en'],
    },
    knowsAbout: [
      'Weighbridges',
      'Weighbridge company in Ghana',
      'Weighbridge manufacturer in India',
      'Industrial weighing scales',
      'Platform scales',
      'Weighing indicators',
      'Weighbridge software',
      'Truck scales',
      'Global industrial weighing supply',
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: COMPANY_INFO.address.office,
      addressLocality: COMPANY_INFO.address.city,
      addressRegion: COMPANY_INFO.address.state,
      postalCode: COMPANY_INFO.address.pincode,
      addressCountry: COMPANY_INFO.address.country,
    },
  }), [primaryEmail, primaryPhone]);

  const websiteSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: COMPANY_INFO.name,
    url: siteUrl,
    description: organizationDescription,
    inLanguage: 'en',
  }), []);

  const webpageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: metadata.title,
    url: canonicalUrl,
    description: metadata.description,
    isPartOf: siteUrl,
    about: [
      'Weighbridges',
      'Truck scales',
      'Industrial weighing scales',
      'Weighing indicators',
      'Weighbridge software',
      'Platform scales',
    ],
  }), [canonicalUrl, metadata.description, metadata.title]);

  const breadcrumbSchema = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbItems = [
      { name: 'Home', url: `${siteUrl}/` },
      ...(pathname !== '/'
        ? segments.map((segment, index) => {
            const path = `/${segments.slice(0, index + 1).join('/')}`;
            const name = product && segment === productSlug
              ? product.name
              : category && segment === categoryId
                ? category.name
                : segment
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

            return {
              name,
              url: `${siteUrl}${path}`,
            };
          })
        : []),
    ];

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  }, [category, categoryId, pathname, product, productSlug]);

  const faqSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pageFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }), [pageFaqs]);

  useEffect(() => {
    setJsonLd('leion-organization-schema', organizationSchema);
    setJsonLd('leion-website-schema', websiteSchema);
    setJsonLd('leion-webpage-schema', webpageSchema);
    setJsonLd('leion-breadcrumb-schema', breadcrumbSchema);
    removeElement('leion-product-schema');
    if (pageFaqs.length) {
      setJsonLd('leion-faq-schema', faqSchema);
    } else {
      removeElement('leion-faq-schema');
    }
  }, [breadcrumbSchema, faqSchema, organizationSchema, pageFaqs, webpageSchema, websiteSchema]);

  return null;
};

export default SiteMetadata;
